import React, { useEffect, useRef, useState } from 'react';
import { Plus, Trash2, Loader2, Image as ImageIcon, Upload, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import settingsService from '../../services/settings.service';

const emptySlide = () => ({ title: '', subtitle: '', ctaLabel: '', ctaUrl: '/', imageUrl: '' });

export default function HeroConfig() {
  const [slides, setSlides] = useState([emptySlide()]);
  const [saving, setSaving] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const fileInputRefs = useRef({});

  useEffect(() => {
    settingsService
      .getHomeConfig()
      .then(data => {
        if (data?.heroSlides?.length) setSlides(data.heroSlides);
      })
      .catch(() => {});
  }, []);

  const addSlide = () => {
    if (slides.length >= 5) return toast.info('Máximo 5 slides');
    setSlides(prev => [...prev, emptySlide()]);
  };

  const removeSlide = idx => {
    if (slides.length === 1) return;
    setSlides(prev => prev.filter((_, i) => i !== idx));
  };

  const update = (idx, field, value) => {
    setSlides(prev => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };

  const handleFileChange = async (idx, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten imágenes');
      return;
    }
    setUploadingIdx(idx);
    try {
      const formData = new FormData();
      const r = Math.random().toString(36).substring(7);
      formData.append('file', file, file.name.replaceAll(' ', '_') + r);
      formData.append('type', 'heroImage');
      const doc = await settingsService.uploadSettingsImg(formData);
      if (!doc?.fileLink) throw new Error('No se recibió la URL de la imagen');
      update(idx, 'imageUrl', doc.fileLink);
      toast.success('Imagen subida');
    } catch (err) {
      console.error(err);
      toast.error('Error al subir la imagen');
    } finally {
      setUploadingIdx(null);
      if (fileInputRefs.current[idx]) fileInputRefs.current[idx].value = '';
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsService.updateHomeConfig({ heroSlides: slides });
      toast.success('Hero actualizado');
    } catch {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='space-y-4'>
      <p className='text-sm text-muted-foreground'>
        Configurá los slides del carrusel hero. El primer slide es el principal. Podés agregar hasta
        5.
      </p>

      <div className='space-y-4'>
        {slides.map((slide, i) => (
          <div key={i} className='rounded-lg border border-border bg-background p-4 space-y-3'>
            <div className='flex items-center justify-between'>
              <span className='text-sm font-semibold text-foreground'>Slide {i + 1}</span>
              {slides.length > 1 && (
                <Button variant='ghost' size='icon-sm' onClick={() => removeSlide(i)}>
                  <Trash2 className='h-4 w-4 text-destructive' />
                </Button>
              )}
            </div>

            {/* Image upload + preview */}
            <div className='space-y-2'>
              <label className='text-xs font-medium text-foreground flex items-center gap-1'>
                <ImageIcon className='h-3.5 w-3.5' /> Imagen de fondo
              </label>
              <input
                ref={el => (fileInputRefs.current[i] = el)}
                type='file'
                accept='image/*'
                className='hidden'
                onChange={e => handleFileChange(i, e)}
              />
              {slide.imageUrl ? (
                <div className='relative h-32 w-full overflow-hidden rounded-md border border-border'>
                  <img
                    src={slide.imageUrl}
                    alt='preview'
                    className='h-full w-full object-cover'
                    onError={e => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <div className='absolute right-2 top-2 flex gap-2'>
                    <Button
                      type='button'
                      variant='secondary'
                      size='icon-sm'
                      onClick={() => fileInputRefs.current[i]?.click()}
                      disabled={uploadingIdx === i}
                    >
                      {uploadingIdx === i ? (
                        <Loader2 className='h-4 w-4 animate-spin' />
                      ) : (
                        <Upload className='h-4 w-4' />
                      )}
                    </Button>
                    <Button
                      type='button'
                      variant='secondary'
                      size='icon-sm'
                      onClick={() => update(i, 'imageUrl', '')}
                      disabled={uploadingIdx === i}
                    >
                      <X className='h-4 w-4 text-destructive' />
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  type='button'
                  onClick={() => fileInputRefs.current[i]?.click()}
                  disabled={uploadingIdx === i}
                  className='flex h-32 w-full flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-border bg-muted/30 text-sm text-muted-foreground transition-colors hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-50'
                >
                  {uploadingIdx === i ? (
                    <>
                      <Loader2 className='h-5 w-5 animate-spin' />
                      <span>Subiendo…</span>
                    </>
                  ) : (
                    <>
                      <Upload className='h-5 w-5' />
                      <span>Hacé click para subir una imagen</span>
                    </>
                  )}
                </button>
              )}
            </div>

            <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
              <div className='space-y-1'>
                <label className='text-xs font-medium text-foreground'>Título</label>
                <Input
                  value={slide.title}
                  onChange={e => update(i, 'title', e.target.value)}
                  placeholder='Título principal'
                />
              </div>
              <div className='space-y-1'>
                <label className='text-xs font-medium text-foreground'>Subtítulo</label>
                <Input
                  value={slide.subtitle}
                  onChange={e => update(i, 'subtitle', e.target.value)}
                  placeholder='Texto secundario'
                />
              </div>
              <div className='space-y-1'>
                <label className='text-xs font-medium text-foreground'>Texto del botón</label>
                <Input
                  value={slide.ctaLabel}
                  onChange={e => update(i, 'ctaLabel', e.target.value)}
                  placeholder='Ver bonos'
                />
              </div>
              <div className='space-y-1'>
                <label className='text-xs font-medium text-foreground'>Link del botón</label>
                <Input
                  value={slide.ctaUrl}
                  onChange={e => update(i, 'ctaUrl', e.target.value)}
                  placeholder='/'
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className='flex gap-2'>
        <Button variant='outline' size='sm' onClick={addSlide} disabled={slides.length >= 5}>
          <Plus className='h-4 w-4 mr-1.5' /> Agregar slide
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving && <Loader2 className='h-4 w-4 mr-2 animate-spin' />}
          Guardar hero
        </Button>
      </div>
    </div>
  );
}
